import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Rating,
  Skeleton,
  CircularProgress
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { addToCart } from '../../store/slices/cartSlice';
import { toggleFavorite, selectIsProductFavorite } from '../../store/slices/favoritesSlice';

const ProductCard = ({ 
  product, 
  onAddToCart, 
  onToggleFavorite, 
  onShare,
  onLoginRequired,
  loading = false,
  showActions = true,
  variant = 'default'
}) => {
  const dispatch = useDispatch();
  
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const isProductFavorite = useSelector(state => 
    product?.id ? selectIsProductFavorite(product.id)(state) : false
  );
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  
  if (!product && !loading) {
    return null;
  }
  
  if (loading) {
    return (
      <Card sx={{ height: '100%', bgcolor: 'white' }}>
        <Skeleton variant="rectangular" height={200} />
        <CardContent>
          <Skeleton variant="text" height={28} />
          <Skeleton variant="text" height={20} />
          <Skeleton variant="text" width={100} height={24} />
        </CardContent>
      </Card>
    );
  }

  const images = Array.isArray(product?.images) 
    ? product.images 
    : product?.images?.items || [];
  
  const getPublicImageUrl = (url) => {
    if (!url) {
      return '/placeholder-product.svg';
    }
    
    let urlString;
    if (typeof url === 'string') {
      urlString = url;
    } else if (url instanceof URL) {
      urlString = url.toString();
    } else if (typeof url === 'object' && url.href) {
      urlString = url.href;
    } else {
      console.warn('getPublicImageUrl received unsupported value:', url, typeof url);
      return '/placeholder-product.svg';
    }
    
    const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
    const region = 'ap-south-1';
    
    if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
      const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
      const filePath = urlParts ? urlParts.split('?')[0] : '';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
    }
    
    return urlString;
  };

  const primaryImageUrl = images.find(img => img.isPrimary)?.url || images[0]?.url;
  const primaryImage = getPublicImageUrl(primaryImageUrl) || '/placeholder-product.svg';
  
  const hasDiscount = product?.discountPrice && product?.discountPrice < product?.price;
  const discountPercentage = hasDiscount 
    ? Math.round(((product.price - product.discountPrice) / product.price) * 100)
    : 0;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    
    if (!product) {
      console.error('Cannot add to cart: No product provided');
      return;
    }
    
    if (onAddToCart) {
      onAddToCart(product);
    } else {
      dispatch(addToCart({ product, quantity: 1 }));
    }
  };

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      if (onLoginRequired) {
        onLoginRequired('favorite', product);
        return;
      }
      alert('Please login to add products to favorites');
      return;
    }

    try {
      setIsTogglingFavorite(true);
      
      if (onToggleFavorite) {
        onToggleFavorite(product);
      } else {
        await dispatch(toggleFavorite(product.id));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    onShare?.(product);
  };

  // Compact variant for special cases
  if (variant === 'compact') {
    return (
      <Card sx={{ display: 'flex', height: 120, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
        <CardMedia
          component="img"
          sx={{ width: 120, height: 120 }}
          image={primaryImage}
          alt={product?.name}
        />
        <CardContent sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle1" noWrap sx={{ color: '#1a202c', fontWeight: 600 }}>
            {product?.name}
          </Typography>
          <Typography variant="body2" noWrap sx={{ color: '#2d3748', fontWeight: 500, mb: 1 }}>
            {product?.shortDescription || product?.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#2b6cb0', fontWeight: 700 }}>
                ₹{hasDiscount ? product?.discountPrice : product?.price}
              </Typography>
              {hasDiscount && (
                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#a0aec0' }}>
                  ₹{product?.price}
                </Typography>
              )}
            </Box>
            {showActions && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  onClick={handleToggleFavorite}
                  disabled={isTogglingFavorite}
                  sx={{
                    bgcolor: 'white',
                    border: '2px solid #e0e0e0',
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: isProductFavorite ? '#ffebee' : '#e3f2fd'
                    }
                  }}
                >
                  {isTogglingFavorite ? (
                    <CircularProgress size={16} />
                  ) : isProductFavorite ? (
                    <FavoriteFilledIcon sx={{ color: '#e53e3e', fontSize: 18 }} />
                  ) : (
                    <FavoriteIcon sx={{ color: '#4a5568', fontSize: 18 }} />
                  )}
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleAddToCart}
                  sx={{
                    bgcolor: 'white',
                    border: '2px solid #e0e0e0',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#e3f2fd' }
                  }}
                >
                  <ShoppingCartIcon sx={{ color: '#2b6cb0', fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Main product card design
  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      bgcolor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: 3,
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        transform: 'translateY(-4px)',
        borderColor: '#cbd5e0'
      }
    }}>
      {/* Image Section */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height={variant === 'detailed' ? 220 : 200}
          image={primaryImage}
          alt={product?.name}
          sx={{ 
            objectFit: 'cover',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}
        />
        
        {/* Top badges */}
        <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
          {product?.isFeatured && (
            <Chip
              label="Featured"
              size="small"
              sx={{ 
                bgcolor: '#ff6b35',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                mb: 0.5,
                display: 'block',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          )}
          {hasDiscount && (
            <Chip
              label={`${discountPercentage}% OFF`}
              size="small"
              sx={{ 
                bgcolor: '#e53e3e',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                display: 'block',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          )}
        </Box>

        {/* Organic badge */}
        {product?.farmingMethod === 'Organic' && (
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
            <Chip
              label="Organic"
              size="small"
              sx={{ 
                bgcolor: '#38a169',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          </Box>
        )}

        {/* Heart icon overlay */}
        {showActions && (
          <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 2 }}>
            <IconButton 
              onClick={handleToggleFavorite}
              disabled={isTogglingFavorite}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                width: 44,
                height: 44,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: isProductFavorite ? '#ffebee' : '#e3f2fd',
                  transform: 'scale(1.1)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                }
              }}
            >
              {isTogglingFavorite ? (
                <CircularProgress size={22} />
              ) : isProductFavorite ? (
                <FavoriteFilledIcon sx={{ color: '#e53e3e', fontSize: 24 }} />
              ) : (
                <FavoriteIcon sx={{ color: '#4a5568', fontSize: 24 }} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Content Section */}
      <CardContent sx={{ flexGrow: 1, p: 3, bgcolor: 'white' }}>
        {/* Product Name */}
        <Typography 
          variant="h6" 
          component="h2" 
          gutterBottom 
          sx={{ 
            color: '#1a202c',
            fontWeight: 700,
            fontSize: '1.125rem',
            lineHeight: 1.3,
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {product?.name}
        </Typography>

        {/* Product Description */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#2d3748',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.6rem'
          }}
        >
          {product?.shortDescription || product?.description}
        </Typography>

        {/* Price Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#2b6cb0',
              fontWeight: 700,
              fontSize: '1.3rem'
            }}
          >
            ₹{hasDiscount ? product?.discountPrice : product?.price}
          </Typography>
          {hasDiscount && (
            <Typography 
              variant="body2" 
              sx={{ 
                textDecoration: 'line-through', 
                color: '#a0aec0',
                ml: 1.5,
                fontSize: '0.875rem'
              }}
            >
              ₹{product?.price}
            </Typography>
          )}
        </Box>

        {/* Category and Rating */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Chip 
            label={
              product?.category?.name?.replace('_', ' ') || 
              (typeof product?.category === 'string' ? product?.category.replace('_', ' ') : 'No Category')
            } 
            size="small" 
            sx={{
              bgcolor: '#edf2f7',
              color: '#4a5568',
              fontWeight: 600,
              borderRadius: 2,
              fontSize: '0.75rem'
            }}
          />
          
          {product?.reviews && product?.reviews.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Rating 
                value={product?.reviews.reduce((acc, review) => acc + review.rating, 0) / product?.reviews.length} 
                precision={0.5} 
                size="small" 
                readOnly 
              />
              <Typography variant="caption" sx={{ ml: 0.5, color: '#718096', fontWeight: 500 }}>
                ({product?.reviews.length})
              </Typography>
            </Box>
          )}
        </Box>

        {/* Stock Info for Detailed Variant */}
        {variant === 'detailed' && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f7fafc', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ color: '#4a5568', mb: 0.5, fontWeight: 500 }}>
              Stock: {product?.stock > 0 ? `${product?.stock} available` : 'Out of stock'}
            </Typography>
            {product?.weight && (
              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                Weight: {product?.weight} kg
              </Typography>
            )}
          </Box>
        )}
      </CardContent>

      {/* Action Section */}
      {showActions && (
        <Box sx={{ p: 3, pt: 0, bgcolor: 'white' }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<ShoppingCartIcon />}
            onClick={handleAddToCart}
            disabled={product?.stock === 0}
            sx={{
              bgcolor: '#2b6cb0',
              color: 'white',
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 4px 12px rgba(43, 108, 176, 0.3)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#2c5282',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(43, 108, 176, 0.4)'
              },
              '&:disabled': {
                bgcolor: '#a0aec0',
                color: 'white',
                boxShadow: 'none'
              }
            }}
          >
            {product?.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default ProductCard;